import { create } from 'zustand';
import { api } from '@/core/api/apiClient';
import type { TaskListItem, TaskSyncEvent } from '@cg-projects/shared';

const CLOSED = new Set(['1_done', '1_canceled']);
// How long a cached board stays trustworthy after you navigate away (its socket
// room is no longer joined, so live events for it stop arriving).
const STALE_MS = 30_000;

interface TasksState {
  tasksByArea: Record<number, TaskListItem[]>;
  loadingArea: Record<number, boolean>;
  errorByArea: Record<number, string | null>;
  /** Tracks whether the cached set for an area includes closed tasks. */
  loadedClosed: Record<number, boolean>;
  /** When each area's cache was filled — drives the staleness refetch. */
  loadedAt: Record<number, number>;
  /** Per-area monotonic load counter — only the newest load may write results. */
  loadSeq: Record<number, number>;
  /** Last time a live event touched an area — drops a stale background refetch. */
  liveAt: Record<number, number>;
  /** Bulk-edit selection (ctrl-click cards / table checkboxes). */
  selectedIds: number[];
  /** Anchor for shift-click range selection — the id of the last card the user
   *  plainly toggled (or range-extended to). Null until the first click. */
  lastSelectedId: number | null;
  toggleSelected: (id: number) => void;
  setSelected: (ids: number[]) => void;
  clearSelected: () => void;
  /** Shift-click: select the inclusive run of `orderedVisibleIds` between
   *  `anchorId` and `targetId` (either direction). ADDITIVE — unions with the
   *  current selection, never deselects, so a shift-click can't silently drop
   *  earlier ctrl-clicked cards. When either end isn't among the visible ids
   *  (filters changed, the card closed, nothing clicked yet) it degrades to a
   *  plain toggle of the target — a dead click would read as "the app broke". */
  selectRange: (anchorId: number, targetId: number, orderedVisibleIds: number[]) => void;
  loadTasks: (areaId: number, includeClosed?: boolean, force?: boolean) => Promise<void>;
  /** Apply a live task:sync event from a teammate (authoritative server state). */
  applyRemote: (e: TaskSyncEvent) => void;
}

export const useTasksStore = create<TasksState>()((set, get) => ({
  tasksByArea: {},
  loadingArea: {},
  errorByArea: {},
  loadedClosed: {},
  loadedAt: {},
  loadSeq: {},
  liveAt: {},
  selectedIds: [],
  lastSelectedId: null,

  toggleSelected: (id) =>
    set((s) => ({
      selectedIds: s.selectedIds.includes(id) ? s.selectedIds.filter((x) => x !== id) : [...s.selectedIds, id],
      // Every plain click re-anchors the next shift-click range — on DEselect
      // too: ranging from the card you just touched is what hands expect.
      lastSelectedId: id,
    })),
  setSelected: (ids) => set({ selectedIds: ids }),
  clearSelected: () => set({ selectedIds: [] }),
  selectRange: (anchorId, targetId, orderedVisibleIds) =>
    set((s) => {
      const a = orderedVisibleIds.indexOf(anchorId);
      const b = orderedVisibleIds.indexOf(targetId);
      // Anchor (or target) not on the visible board → fall back to a plain toggle.
      if (a < 0 || b < 0) {
        return {
          selectedIds: s.selectedIds.includes(targetId)
            ? s.selectedIds.filter((x) => x !== targetId)
            : [...s.selectedIds, targetId],
          lastSelectedId: targetId,
        };
      }
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      const selectedIds = [...s.selectedIds];
      for (const id of orderedVisibleIds.slice(lo, hi + 1)) {
        if (!selectedIds.includes(id)) selectedIds.push(id);
      }
      // Anchor moves to the target so a chain of shift-clicks extends naturally.
      return { selectedIds, lastSelectedId: targetId };
    }),

  loadTasks: async (areaId, includeClosed = false, force = false) => {
    const st = get();
    const cached = st.tasksByArea[areaId] && st.loadedClosed[areaId] === includeClosed;
    // A cached board is only trustworthy while its socket room was joined. Once
    // you switch away, live events for it stop arriving — so coming back to a
    // cache older than STALE_MS refetches in the BACKGROUND (cards stay on
    // screen, no spinner flash) instead of showing a frozen board forever.
    const fresh = cached && Date.now() - (st.loadedAt[areaId] ?? 0) < STALE_MS;
    if (!force && fresh) return;

    const background = cached && !force;
    // Sequencing: only the newest load for an area may write results, so a slow
    // load can't clobber a fresher one (e.g. rapid Show-completed toggles).
    const seq = (get().loadSeq[areaId] ?? 0) + 1;
    // A BACKGROUND refetch is a snapshot that predates any live event landing
    // during it — capture the start time so we can drop its stale whole-board
    // write if applyRemote updated the board meanwhile. A FOREGROUND load
    // (first open, Show-completed, force) is authoritative and always applies.
    const startedAt = Date.now();
    set((s) => ({ loadSeq: { ...s.loadSeq, [areaId]: seq } }));
    if (!background) {
      set((s) => ({
        loadingArea: { ...s.loadingArea, [areaId]: true },
        errorByArea: { ...s.errorByArea, [areaId]: null },
      }));
    }
    try {
      const qs = includeClosed ? '?includeClosed=true' : '';
      const { tasks } = await api.get<{ tasks: TaskListItem[] }>(`/areas/${areaId}/tasks${qs}`);
      if (get().loadSeq[areaId] !== seq) return; // superseded by a newer load
      // A live event landed during a BACKGROUND refetch → its snapshot is stale;
      // keep the live-updated board instead of reverting it.
      if (background && (get().liveAt[areaId] ?? 0) > startedAt) {
        set((s) => ({ loadingArea: { ...s.loadingArea, [areaId]: false }, loadedAt: { ...s.loadedAt, [areaId]: Date.now() } }));
        return;
      }
      set((s) => ({
        tasksByArea: { ...s.tasksByArea, [areaId]: tasks },
        loadedClosed: { ...s.loadedClosed, [areaId]: includeClosed },
        loadedAt: { ...s.loadedAt, [areaId]: Date.now() },
        loadingArea: { ...s.loadingArea, [areaId]: false },
        // Success always clears a prior error — otherwise a stale errorByArea
        // keeps the board on its error screen forever.
        errorByArea: { ...s.errorByArea, [areaId]: null },
      }));
    } catch (err) {
      if (get().loadSeq[areaId] !== seq) return;
      // A refetch failing must NOT tear down a board that already has cards on
      // screen — a transient blip is survivable, and this covers BOTH the
      // background staleness refetch AND a force=true refetch (socket reconnect,
      // area:refresh, a failed rollback re-read). Only surface the full-screen
      // error when there is genuinely nothing to show.
      const hasCards = (get().tasksByArea[areaId]?.length ?? 0) > 0;
      set((s) => ({
        loadingArea: { ...s.loadingArea, [areaId]: false },
        ...(hasCards ? {} : { errorByArea: { ...s.errorByArea, [areaId]: err instanceof Error ? err.message : 'Failed to load tasks.' } }),
      }));
    }
  },

  applyRemote: (e) => {
    const { descriptionHtml: _d, children: _c, blockedBy: _b, blocks: _bl, ...item } = e.task;
    // Stamp the affected areas as live-touched: an in-flight BACKGROUND refetch
    // that started before now will see this and drop its stale snapshot, keeping
    // the live update. This does NOT cancel a FOREGROUND load (first open,
    // Show-completed, force) — those are authoritative and must always apply.
    const now = Date.now();
    set((s) => {
      const liveAt = { ...s.liveAt };
      for (const aid of [e.areaId, e.prevAreaId]) if (aid) liveAt[aid] = now;
      return { liveAt };
    });
    set((s) => {
      const tba = { ...s.tasksByArea };
      // Left its old area? Drop it there.
      if (e.prevAreaId && e.prevAreaId !== e.areaId && tba[e.prevAreaId]) {
        tba[e.prevAreaId] = tba[e.prevAreaId].filter((t) => t.id !== item.id);
      }
      const list = tba[e.areaId];
      if (list) {
        const hideClosed = !s.loadedClosed[e.areaId];
        const i = list.findIndex((t) => t.id === item.id);
        if (CLOSED.has(item.state) && hideClosed) {
          if (i >= 0) tba[e.areaId] = list.filter((t) => t.id !== item.id);
        } else if (i >= 0) {
          const copy = [...list];
          copy[i] = item as TaskListItem;
          tba[e.areaId] = copy;
        } else if (!item.parentId) {
          tba[e.areaId] = [item as TaskListItem, ...list];
        }
      }
      return { tasksByArea: tba };
    });
  },
}));
